import { useState, useEffect, useCallback } from 'react';
import { userAPI, User, CreateUserData, UpdateUserData } from '../api/users';

export const useUsers = () => {
  const [users, setUsers] = useState<User[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchUsers = async () => {
      setIsLoading(true);
      setError(null);
      try {
        const data = await userAPI.getAll();
        setUsers(data);
      } catch (error) {
        console.error('Failed to fetch users:', error);
        setError('Erreur lors du chargement des utilisateurs');
      } finally {
        setIsLoading(false);
      }
    };

    fetchUsers();
  }, []);

  const createUser = useCallback(async (userData: CreateUserData) => {
    setError(null);
    try {
      const createdUser = await userAPI.create(userData);
      setUsers(prev => [createdUser, ...prev]);
      return true;
    } catch (error) {
      console.error('Failed to create user:', error);
      setError(error instanceof Error ? error.message : 'Erreur lors de la création');
      return false;
    }
  }, []);

  const updateUser = useCallback(async (userId: number, userData: UpdateUserData) => {
    setError(null);
    try {
      const updatedUser = await userAPI.update(userId, userData);
      setUsers(prev => prev.map(user => 
        user.id === userId ? updatedUser : user
      ));
      return true;
    } catch (error) {
      console.error('Failed to update user:', error);
      setError(error instanceof Error ? error.message : 'Erreur lors de la mise à jour');
      return false;
    }
  }, []);

  const deleteUser = useCallback(async (userId: number) => {
    setError(null);
    try {
      await userAPI.delete(userId);
      setUsers(prev => prev.filter(user => user.id !== userId));
      return true;
    } catch (error) {
      console.error('Failed to delete user:', error);
      setError(error instanceof Error ? error.message : 'Erreur lors de la suppression');
      return false;
    }
  }, []);

  const suspendUser = useCallback(async (userId: number) => {
    setError(null);
    try {
      await userAPI.suspend(userId);
      setUsers(prev => prev.map(user => 
        user.id === userId 
          ? { ...user, status: 'suspended' }
          : user
      ));
      return true;
    } catch (error) {
      console.error('Failed to suspend user:', error);
      setError(error instanceof Error ? error.message : 'Erreur lors de la suspension');
      return false;
    }
  }, []);

  const activateUser = useCallback(async (userId: number) => {
    setError(null);
    try {
      await userAPI.activate(userId);
      setUsers(prev => prev.map(user => 
        user.id === userId 
          ? { ...user, status: 'active' }
          : user
      ));
      return true;
    } catch (error) {
      console.error('Failed to activate user:', error);
      setError(error instanceof Error ? error.message : 'Erreur lors de l\'activation');
      return false;
    }
  }, []);

  const changeUserRole = useCallback(async (userId: number, role: 'admin' | 'agency_owner' | 'agent') => {
    setError(null);
    try {
      await userAPI.changeRole(userId, role);
      setUsers(prev => prev.map(user => 
        user.id === userId 
          ? { ...user, role }
          : user
      ));
      return true;
    } catch (error) {
      console.error('Failed to change user role:', error);
      setError(error instanceof Error ? error.message : 'Erreur lors du changement de rôle');
      return false;
    }
  }, []);
  return {
    users,
    isLoading,
    error,
    createUser,
    updateUser,
    deleteUser,
    suspendUser,
    activateUser,
    changeUserRole,
  };
};